import ApiService from './ApiService';

const CATEGORY_ORDER = [
  'ChildhoodMemories', 'TeenageYears', 'FamilyTraditions', 'LifeLessons',
  'CareerAndWork', 'HistoricalEvents', 'CulturalAndSocialPerspectives',
  'FamilyHistory', 'HealthAndWellness', 'TravelAndAdventures',
  'LoveAndRelationships', 'HobbiesAndPassions', 'CreativeAndArtisticExpression',
  'Personality', 'ExperiencesAndMilestones', 'SpiritualityAndReligion',
  'FutureGoalsAndAspirations',
];

const SCENARIO_MAP = {
  qa: 'InterviewAdult',
  story: 'Story',
  moment: 'Moment',
  callout: 'WeeklyCallout',
};

/**
 * Filter questions for Q&A mode: active, matching scenario, unanswered by this relative.
 */
function filterQAQuestions(allQuestions, answeredUuids, scenario = 'InterviewAdult') {
  return allQuestions.filter(q =>
    q.status === 'active' &&
    (q.questionScenario || []).includes(scenario) &&
    !answeredUuids.has(q.uuid)
  );
}

/**
 * Group questions by category, sorted by CATEGORY_ORDER.
 */
function groupByCategory(questions) {
  const groups = {};
  questions.forEach(q => {
    const cat = q.questionCategory || 'Other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(q);
  });

  const sortedEntries = Object.entries(groups).sort(([a], [b]) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    const aOrder = ai >= 0 ? ai : CATEGORY_ORDER.length;
    const bOrder = bi >= 0 ? bi : CATEGORY_ORDER.length;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.localeCompare(b);
  });

  return sortedEntries;
}

/**
 * Build a FormData object for uploading recorded content.
 */
function buildUploadFormData({ videoBlob, question, familyUuids, mode, caption, storySessionUuid }) {
  const formData = new FormData();
  formData.append('contentType', 'video');
  formData.append('familyUuid', JSON.stringify(familyUuids));

  const scenario = SCENARIO_MAP[mode] || 'InterviewAdult';
  formData.append('questionScenario', scenario);

  if (mode === 'moment') {
    const momentQuestion = {
      uuid: crypto.randomUUID(),
      question: '',
      summary: '',
      questionCategory: 'Moment',
      questionScenario: ['Moment'],
      questionType: 'custom',
    };
    formData.append('questionUuid', JSON.stringify(momentQuestion));
  } else if (question) {
    const questionObj = {
      uuid: question.uuid,
      question: question.question,
      summary: question.summary || '',
      questionCategory: question.questionCategory || '',
      questionScenario: question.questionScenario || [],
      questionType: mode === 'story' ? 'story' : 'upload',
    };
    if (storySessionUuid) {
      questionObj.storySessionUuid = storySessionUuid;
    }
    formData.append('questionUuid', JSON.stringify(questionObj));
  }

  formData.append('file', videoBlob, 'recording.webm');

  // Debug: log FormData contents
  console.log('Upload FormData:', {
    contentType: 'video',
    familyUuids,
    scenario,
    mode,
    blobSize: `${(videoBlob.size / 1024 / 1024).toFixed(2)} MB`,
    blobType: videoBlob.type,
  });

  return formData;
}

/**
 * Get the best supported MIME type for MediaRecorder.
 */
function getSupportedMimeType() {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  return candidates.find(t => MediaRecorder.isTypeSupported(t)) || '';
}

/**
 * Poll for next story question with retry logic.
 * Returns the question object or throws on failure.
 */
async function pollForStoryQuestion(sessionUuid, maxAttempts = 60, intervalMs = 3000) {
  for (let i = 0; i < maxAttempts; i++) {
    const { status, data } = await ApiService.pollStoryNextQuestion(sessionUuid);
    if (status === 200) return data;
    if (status >= 400 && status !== 404) throw new Error(data.error || 'Failed to generate question');
    await new Promise(r => setTimeout(r, intervalMs));
  }
  throw new Error('Timed out waiting for next question');
}

/**
 * Get family UUIDs for a relative.
 */
function getFamilyUuids(relative, families) {
  if (!relative.families || relative.families.length === 0) {
    return families.map(f => f.uuid);
  }
  return relative.families.map(f => f.uuid);
}

export {
  CATEGORY_ORDER,
  SCENARIO_MAP,
  filterQAQuestions,
  groupByCategory,
  buildUploadFormData,
  getSupportedMimeType,
  pollForStoryQuestion,
  getFamilyUuids,
};
